import { useCallback, useEffect, useRef } from 'react';
import { collect } from '@/modules/Collect';
import { type ChatTimeoutClientStage, createChatTimeoutStageTracker } from './chatTimeoutStage';

const CHAT_TIMEOUT_DURATION = 10 * 60 * 1000;

export interface UseChatTimeoutOptions {
  /** 项目 ID */
  projectId: string | null;
}

/**
 * 对话超时检测 Hook
 *
 * 功能：
 * 1. 启动对话后开始计时（10分钟）
 * 2. 对话完成或出错时清除定时器
 * 3. 超时后自动上报 chat_timeout 埋点
 * 4. 组件卸载时自动清理定时器
 *
 * @example
 * ```tsx
 * const { startTimer, clearTimer } = useChatTimeout({ projectId });
 *
 * // 对话开始时
 * onConversationStart: (messageId) => {
 *   startTimer(messageId);
 * }
 *
 * // 对话完成时
 * onComplete: () => {
 *   clearTimer();
 * }
 * ```
 */
export function useChatTimeout({ projectId }: UseChatTimeoutOptions) {
  const timerRef = useRef<{ id: number; messageId: string } | null>(null);
  const stageTrackerRef = useRef(createChatTimeoutStageTracker());

  /**
   * 启动超时定时器
   * @param messageId - 消息 ID
   */
  const startTimer = useCallback((messageId: string) => {
    // 清除旧的定时器
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current.id);
    }

    stageTrackerRef.current.reset();

    // 启动新的定时器
    const timerId = window.setTimeout(() => {
      if (timerRef.current?.id !== timerId) {
        return;
      }

      collect('chat_timeout', {
        message_id: messageId,
        project_id: projectId,
        timeout: CHAT_TIMEOUT_DURATION,
        last_client_stage: stageTrackerRef.current.get(),
      });
      timerRef.current = null;
    }, CHAT_TIMEOUT_DURATION);
    timerRef.current = { id: timerId, messageId };
  }, [projectId]);

  const setClientStage = useCallback((messageId: string, stage: ChatTimeoutClientStage) => {
    if (timerRef.current?.messageId === messageId) {
      stageTrackerRef.current.set(stage);
    }
  }, []);

  /**
   * 清除超时定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current.id);
      timerRef.current = null;
    }
  }, []);

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return { startTimer, clearTimer, setClientStage };
}
